<article
  class="check-card-container"
  [attr.aria-label]="getCardAriaLabel()">

  <header class="important-header" *ngIf="item.globalObjectType === objectTypes.Job && this.item.important">
    <i class="material-symbols-outlined important-icon" aria-hidden="true">kid_star</i>
    <span class="visually-hidden">This check is marked as important</span>
    <p aria-hidden="true">{{ T.common.important | translate }}</p>
  </header>

  <div class="card-inner big">
    <header class="card-top">
      <div class="card-top-left">
        <div class="draggable-wrapper" *ngIf="draggable" aria-hidden="true">
          <div class="responsive-flex-wrapper">
            <div class="material-symbols-outlined">drag_handle</div>
          </div>
        </div>
        <div class="titles">
          <h3
            class="primary-title"
            [app-tooltip]="{ message: item.title, position: ['top'] }"
            [class.not-applicable-title]="item.isNotApplicable"
            [attr.id]="getTitleId()"
            [attr.aria-label]="'Title: ' + item.title + (item.starred ? '. This check is required.' : '') ">
            <span class="w-100" aria-hidden="true">{{ item.title }}</span>
            <span class="required-card" *ngIf="item.starred">
              <span aria-hidden="true">* {{ T.common.required.one | translate }}</span>
            </span>
          </h3>
          <p
            class="sub-title"
            *ngIf="item.description && !item.isNotApplicable"
            [attr.id]="getDescriptionId()">
            <span class="visually-hidden">Description: </span>
            <span class="w-100">{{ item.description | htmlString }}</span>
          </p>
        </div>
      </div>
      <ng-container *ngIf="userHasWriteAccess">
        <nav class="card-top-right" aria-label="Check item actions">
          <button
            type="button"
            *ngIf="checklistType !== checklistTypes.MyTrack"
            class="card-button card-attachment-btn"
            (click)="openAttachmentsSection()"
            [attr.aria-label]="getAttachmentAriaLabel()">
            <i class="material-symbols-outlined small" aria-hidden="true">attach_file</i>
            <span class="tiny-int" aria-hidden="true">{{
              item && item.attachmentsCount ? item.attachmentsCount : 0
            }}</span>
          </button>
          <button
            type="button"
            *ngIf="item.globalObjectType === objectTypes.Job"
            class="more-button"
            [closeUponSelection]="true"
            ngDropdown
            [templateRef]="moreButtonTemplate"
            [attr.aria-label]="getMenuButtonAriaLabel()"
            aria-haspopup="menu"
            [attr.aria-expanded]="isMenuOpen"
            aria-controls="actions-menu"
            (click)="onMenuButtonClick($event)"
            (keydown.enter)="onMenuButtonClick($event)"
            (keydown.space)="onMenuButtonClick($event); $event.preventDefault()">
            <i class="material-symbols-outlined check-icon" aria-hidden="true">more_horiz</i>
          </button>
        </nav>
      </ng-container>
    </header>

    <section
      class="card-bottom"
      *ngIf="item.isNotApplicable"
      role="status"
      aria-live="polite">
      <span class="visually-hidden">This check is marked as not applicable</span>
      <div class="not-applicable-container text-center" aria-hidden="true">
        <span>{{T.common.marked_not_applicable | translate}}</span>
      </div>
    </section>

    <section
      class="card-bottom"
      *ngIf="!item.isNotApplicable"
      [attr.aria-labelledby]="getTitleId()"
      [attr.aria-describedby]="item.description ? getDescriptionId() : null">

      <!-- Mark as Complete -->
      <div *ngIf="item.responseType === responseTypes.Mark_as_Complete">
        <div class="checkbox-container" (click)="$event.stopPropagation()">
          <app-checkbox-with-label
            role="checkbox"
            (clicked)="onCheckComplete($event)"
            [intialState]="item.completed ? 'checked' : 'unchecked'"
            [disabled]="loading || !userHasWriteAccess"
            [label]="T.control.mark_as_complete | translate"
            [ariaLabel]="getCheckboxAriaLabel()">
          </app-checkbox-with-label>
        </div>
      </div>

      <!-- Text Response -->
      <div *ngIf="item.responseType === responseTypes.Text_Response">
        <app-rich-text-editor-v2
          [disabled]="loading || !userHasWriteAccess"
          [inputText]="item.response"
          [showUndoAndRedo]="false"
          [showProfileImage]="false"
          [placeholder]="'Write a response...'"
          (saved)="saveInputResponse($event)"
          [objectId]="item.id"
          [ariaLabel]="getTextResponseAriaLabel()">
        </app-rich-text-editor-v2>
      </div>

      <!-- Numeric Response -->
      <div *ngIf="item.responseType === responseTypes.Numeric_Response">
        <app-text-field
          [(ngModel)]="item.response"
          [placeholder]="'Numeric Response'"
          [restrictedCharacters]="['e', 'E', '+', '-']"
          [disabled]="loading || !userHasWriteAccess"
          type="number"
          (blur)="saveNumericResponse()"
          [ariaLabel]="getNumericResponseAriaLabel()"
          inputmode="numeric">
        </app-text-field>
      </div>

      <!-- Options Set 1 (Status Check) -->
      <fieldset
        class="d-flex justify-content-between"
        *ngIf="item.responseType === responseTypes.Options_Set_1"
        [attr.aria-labelledby]="getTitleId()">
        <legend class="visually-hidden">{{ item.title }} - Status selection</legend>
        <ng-container *ngFor="let optionSet1Item of optionSet1Items">
          <app-button-rectangle
            class="option-btn"
            [ngClass]="optionSet1Item === optionSet1Items[2] ? 'mr-0' : ''"
            [text]="optionSet1Item.value"
            [disabled]="loading || !userHasWriteAccess"
            [buttonTheme]="optionSet1Item.value.toLowerCase()"
            [selected]="item.response === optionSet1Item.key.toString()"
            (clicked)="selectType(optionSet1Item)"
            [fitToContainer]="true"
            role="button"
            [attr.aria-label]="getButtonAriaLabel(optionSet1Item.value)"
            [attr.aria-pressed]="item.response === optionSet1Item.key.toString()">
          </app-button-rectangle>
        </ng-container>
      </fieldset>

      <!-- Options Set 2 (Quality Check) -->
      <fieldset
        class="d-flex justify-content-between"
        *ngIf="item.responseType === responseTypes.Options_Set_2"
        [attr.aria-labelledby]="getTitleId()">
        <legend class="visually-hidden">{{ item.title }} - Quality selection</legend>
        <ng-container *ngFor="let optionSet2Item of optionSet2Items; let i = index">
          <app-button-rectangle
            class="option-btn"
            [ngClass]="optionSet2Item === optionSet2Items[2] ? 'mr-0' : ''"
            [text]="optionSet2Item.value"
            [disabled]="loading || !userHasWriteAccess"
            [buttonTheme]="optionSet1Items[i].value.toLowerCase()"
            [selected]="item.response === optionSet2Item.key.toString()"
            (clicked)="selectType(optionSet2Item)"
            [fitToContainer]="true"
            role="button"
            [attr.aria-label]="getButtonAriaLabel(optionSet2Item.value)"
            [attr.aria-pressed]="item.response === optionSet2Item.key.toString()">
          </app-button-rectangle>
        </ng-container>
      </fieldset>

      <!-- Additional Text Response -->
      <div *ngIf="showTextResponseField">
        <app-rich-text-editor-v2
          [disabled]="loading"
          [inputText]="item.textResponse"
          [showUndoAndRedo]="false"
          [showProfileImage]="false"
          [disabled]="!userHasWriteAccess"
          [placeholder]="'Write a response...'"
          (saved)="saveTextResponse($event)"
          [objectId]="item.id"
          class="w-100"
          [ariaLabel]="getTextResponseAriaLabel(true)">
        </app-rich-text-editor-v2>
      </div>

      <!-- Attachments -->
      @if(attachmentsHasBeenInitialized && checklistType !== checklistTypes.MyTrack) {
        <section class="mt-8" aria-label="Attachments">
          <app-document-manager-general-settings
            [objectId]="item?.id"
            [objectType]="objectType"
            [object]="parentItem"
            [filterType]="filterTypes.Job"
            [isInAccordion]="true"
            [isDetailsPage]="true"
            [showListBoardButtons]="false"
            [showCategoryDropdowns]="false"
            [contextualInfo]="getChecklistItemContextualInfo()"
            (documentsChanged)="onDocumentsChanged($event)">
          </app-document-manager-general-settings>
        </section>
      }
    </section>

    <!-- Owner Lozenge (Mobile) -->
    <footer *ngIf="isMobile" class="mobile-owner-lozenge">
      <app-card-lozenge
        *ngIf="showOwnerLozenge && hasOwner"
        [class.mr-16]="item.responseType === responseTypes.Mark_as_Complete || item.starred"
        [canEdit]="userHasWriteAccess"
        [objectType]="objectType"
        [allAppliedFilters]="filterSettings"
        [filters]="filterSettings"
        [filterTypeSelector]="ownerFilterTypeSelector"
        (lozengeUpdates)="updateFilters($event)">
      </app-card-lozenge>
    </footer>
  </div>

  <ng-template #moreButtonTemplate>
    <menu
      id="actions-menu"
      class="dropdown-options-container"
      role="menu"
      [attr.aria-label]="item.title + ' actions menu'">
      <li role="none">
        <button
          type="button"
          class="dropdown-option"
          role="menuitem"
          tabindex="0"
          *ngIf="item.globalObjectType === objectTypes.Job && !this.item.isNotApplicable && !item.starred"
          (click)="changeApplicableState(); onMenuClose()"
          (keydown.escape)="onMenuClose()">
          {{ T.common.mark_as_not_applicable | translate }}
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          class="dropdown-option"
          role="menuitem"
          tabindex="0"
          *ngIf="item.globalObjectType === objectTypes.Job && this.item.isNotApplicable"
          (click)="changeApplicableState(); onMenuClose()"
          (keydown.escape)="onMenuClose()">
          {{ T.common.mark_as_applicable | translate }}
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          class="dropdown-option"
          role="menuitem"
          tabindex="0"
          *ngIf="item.globalObjectType === objectTypes.Job && !this.item.important"
          (click)="changeImportantState(); onMenuClose()"
          (keydown.escape)="onMenuClose()">
          {{ T.common.mark_as_important | translate }}
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          class="dropdown-option"
          role="menuitem"
          tabindex="0"
          *ngIf="item.globalObjectType === objectTypes.Job && this.item.important"
          (click)="changeImportantState(); onMenuClose()"
          (keydown.escape)="onMenuClose()">
          {{ T.common.mark_as_not_important | translate }}
        </button>
      </li>
    </menu>
  </ng-template>
</article>
